import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { 
  Plus, Edit, Trash2, Save, RefreshCw, Eye, EyeOff,
  HelpCircle, Trophy, Users, Building, MapPin, BookOpen,
  CheckCircle, Info, Lightbulb, GraduationCap, Settings,
  ArrowUp, ArrowDown
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const iconOptions = [
  { value: 'HelpCircle', label: 'Help Circle', icon: HelpCircle },
  { value: 'Trophy', label: 'Trophy', icon: Trophy },
  { value: 'Users', label: 'Users', icon: Users },
  { value: 'Building', label: 'Building', icon: Building },
  { value: 'MapPin', label: 'Map Pin', icon: MapPin },
  { value: 'BookOpen', label: 'Book', icon: BookOpen },
  { value: 'CheckCircle', label: 'Check', icon: CheckCircle },
  { value: 'Info', label: 'Info', icon: Info },
  { value: 'Lightbulb', label: 'Lightbulb', icon: Lightbulb },
  { value: 'GraduationCap', label: 'Graduation', icon: GraduationCap }
];

const categoryOptions = [
  { value: 'general', label: 'Getting Started' },
  { value: 'rank', label: 'JEE Rank' },
  { value: 'category', label: 'Categories' },
  { value: 'counselling', label: 'Counselling' },
  { value: 'state', label: 'Home State' },
  { value: 'payment', label: 'Payment' }
];

const FormInstructionsAdmin = () => {
  const [instructions, setInstructions] = useState([]);
  const [settings, setSettings] = useState({
    enabled: true,
    show_in_menu: true,
    show_on_form: true,
    page_title: 'JEE Form Guide',
    page_description: 'Step-by-step instructions for filling the JEE College Predictor form'
  });
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingInstruction, setEditingInstruction] = useState(null);
  const [form, setForm] = useState({
    title: '',
    content: '',
    category: 'general',
    order: 0,
    icon: 'HelpCircle',
    is_active: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/admin/instructions`);
      const data = await res.json();
      if (data.success) {
        setInstructions(data.instructions);
        setSettings(data.settings);
      }
    } catch (error) {
      console.error('Error fetching instructions:', error);
      toast({ title: 'Error', description: 'Failed to fetch instructions', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/instructions/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });
      const data = await res.json();
      if (data.success) {
        toast({ title: 'Success', description: 'Settings saved' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save settings', variant: 'destructive' });
    }
  };

  const handleSaveInstruction = async () => {
    if (!form.title || !form.content) {
      toast({ title: 'Error', description: 'Title and content are required', variant: 'destructive' });
      return;
    }

    try {
      const url = editingInstruction 
        ? `${API_URL}/api/admin/instructions/${editingInstruction.id}`
        : `${API_URL}/api/admin/instructions`;
      
      const res = await fetch(url, {
        method: editingInstruction ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      
      const data = await res.json();
      if (data.success) {
        toast({ title: 'Success', description: editingInstruction ? 'Instruction updated' : 'Instruction created' });
        setShowModal(false);
        resetForm();
        fetchData();
      } else {
        toast({ title: 'Error', description: data.detail || 'Failed to save', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save instruction', variant: 'destructive' });
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this instruction?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/instructions/${id}`, {
        method: 'DELETE'
      });
      const data = await res.json();
      if (data.success) {
        toast({ title: 'Success', description: 'Instruction deleted' });
        fetchData();
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' });
    }
  };

  const handleToggleActive = async (instruction) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/instructions/${instruction.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !instruction.is_active })
      });
      const data = await res.json();
      if (data.success) {
        fetchData();
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update', variant: 'destructive' });
    }
  };

  const openEdit = (instruction) => {
    setEditingInstruction(instruction);
    setForm({
      title: instruction.title,
      content: instruction.content,
      category: instruction.category,
      order: instruction.order,
      icon: instruction.icon,
      is_active: instruction.is_active
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingInstruction(null);
    setForm({
      title: '',
      content: '',
      category: 'general',
      order: instructions.length,
      icon: 'HelpCircle',
      is_active: true
    });
  };

  const getIconComponent = (iconName) => {
    const found = iconOptions.find(i => i.value === iconName);
    return found ? found.icon : HelpCircle;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-slate-800">Form Instructions Manager</h2>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Button size="sm" onClick={() => { resetForm(); setShowModal(true); }}>
            <Plus className="w-4 h-4 mr-2" /> Add Instruction
          </Button>
        </div>
      </div>

      {/* Settings Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" /> Display Settings
          </CardTitle>
          <CardDescription>Control how and where instructions are displayed</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="font-medium">Enable Instructions</Label>
                  <p className="text-sm text-slate-500">Show/hide the entire guide section</p>
                </div>
                <Switch 
                  checked={settings.enabled}
                  onCheckedChange={(v) => setSettings({ ...settings, enabled: v })}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label className="font-medium">Show in Menu</Label>
                  <p className="text-sm text-slate-500">Add "Help/Guide" link to navigation</p>
                </div>
                <Switch 
                  checked={settings.show_in_menu}
                  onCheckedChange={(v) => setSettings({ ...settings, show_in_menu: v })}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label className="font-medium">Show on Form</Label>
                  <p className="text-sm text-slate-500">Display help tooltips on form fields</p>
                </div>
                <Switch 
                  checked={settings.show_on_form}
                  onCheckedChange={(v) => setSettings({ ...settings, show_on_form: v })}
                />
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <Label>Page Title</Label>
                <Input 
                  value={settings.page_title}
                  onChange={(e) => setSettings({ ...settings, page_title: e.target.value })}
                  placeholder="JEE Form Guide"
                />
              </div>
              <div>
                <Label>Page Description</Label>
                <Textarea 
                  value={settings.page_description}
                  onChange={(e) => setSettings({ ...settings, page_description: e.target.value })}
                  placeholder="Step-by-step instructions..."
                  rows={2}
                />
              </div>
            </div>
          </div>
          <Button onClick={handleSaveSettings}>
            <Save className="w-4 h-4 mr-2" /> Save Settings
          </Button>
        </CardContent>
      </Card>

      {/* Instructions List */}
      <Card>
        <CardHeader>
          <CardTitle>Instructions ({instructions.length})</CardTitle>
          <CardDescription>Manage form help content. Drag to reorder or edit each item.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {instructions.map((instruction, idx) => {
              const Icon = getIconComponent(instruction.icon);
              return (
                <div 
                  key={instruction.id}
                  className={`p-4 border rounded-lg ${instruction.is_active ? 'bg-white' : 'bg-slate-50 opacity-60'}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Icon className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-semibold text-slate-800">{instruction.title}</h4>
                          <Badge variant="outline" className="text-xs">
                            {categoryOptions.find(c => c.value === instruction.category)?.label || instruction.category}
                          </Badge>
                          {!instruction.is_active && (
                            <Badge variant="secondary" className="text-xs">Hidden</Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-600 line-clamp-2">{instruction.content}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => handleToggleActive(instruction)}
                        title={instruction.is_active ? 'Hide' : 'Show'}
                      >
                        {instruction.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => openEdit(instruction)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => handleDelete(instruction.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
            {instructions.length === 0 && (
              <div className="text-center py-8 text-slate-500">
                <HelpCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No instructions yet. Click "Add Instruction" to create one.</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingInstruction ? 'Edit Instruction' : 'Add New Instruction'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title *</Label>
              <Input 
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g., What is JEE Rank?"
              />
            </div>
            <div>
              <Label>Content *</Label>
              <Textarea 
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                placeholder="Enter detailed explanation..."
                rows={5}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categoryOptions.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Icon</Label>
                <Select value={form.icon} onValueChange={(v) => setForm({ ...form, icon: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {iconOptions.map(icon => {
                      const IconComp = icon.icon;
                      return (
                        <SelectItem key={icon.value} value={icon.value}>
                          <div className="flex items-center gap-2">
                            <IconComp className="w-4 h-4" />
                            {icon.label}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Display Order</Label>
                <Input 
                  type="number"
                  value={form.order}
                  onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) })}
                />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <Switch 
                  checked={form.is_active}
                  onCheckedChange={(v) => setForm({ ...form, is_active: v })}
                />
                <Label>Active (visible to users)</Label>
              </div>
            </div>
            <Button onClick={handleSaveInstruction} className="w-full">
              <Save className="w-4 h-4 mr-2" /> {editingInstruction ? 'Update' : 'Create'} Instruction
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FormInstructionsAdmin;
